import { TEAM, IMAGES, DOCTOR_BIO, STORY } from "../data/site";
import Reveal from "../components/Reveal";
import { PageHero, CallToAction } from "../components/Sections";

const StoryParagraphs = ({ items }) => (
  <div className="space-y-5">
    {items.map((p) => (
      <p key={p.slice(0, 40)} className="text-muted-foreground text-base leading-relaxed">
        {p}
      </p>
    ))}
  </div>
);

const StorySignature = () => (
  <div data-testid="story-signature" className="flex items-center gap-5 mt-12 pt-8 border-t border-border">
    <img
      src={STORY.signature.photo}
      alt={STORY.signature.name}
      className="w-16 h-16 rounded-full object-cover object-top"
    />
    <div>
      <p className="font-serif text-xl font-semibold text-navy">— {STORY.signature.name}</p>
      <p className="text-tiffany text-sm font-medium">{STORY.signature.role}</p>
      <p className="text-muted-foreground text-sm">{STORY.signature.practice}</p>
    </div>
  </div>
);

const StorySection = () => (
  <section data-testid="our-story-section" className="py-24 sm:py-28">
    <div className="max-w-3xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="text-center mb-12">
          <p className="text-tiffany uppercase tracking-[0.3em] text-xs font-semibold mb-4">
            {STORY.eyebrow}
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-navy">
            {STORY.title}
          </h2>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <StoryParagraphs items={STORY.part1} />
      </Reveal>
      <Reveal delay={0.1}>
        <h3 className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-navy mt-12 mb-5">
          {STORY.subheading}
        </h3>
        <StoryParagraphs items={STORY.part2} />
      </Reveal>
      <Reveal delay={0.1}>
        <p
          data-testid="story-highlight"
          className="font-serif text-2xl sm:text-3xl font-semibold text-tiffany text-center my-10"
        >
          {STORY.highlight}
        </p>
        <StoryParagraphs items={STORY.part3} />
      </Reveal>
      <Reveal delay={0.1}>
        <StorySignature />
      </Reveal>
    </div>
  </section>
);

const DoctorBio = () => (
  <section data-testid="doctor-bio-section" className="bg-muted py-24 sm:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
      <Reveal className="lg:col-span-2">
        <div className="relative aspect-[4/5] rounded-sm overflow-hidden max-w-md mx-auto lg:mx-0">
          <img
            src={DOCTOR_BIO.photo}
            alt={DOCTOR_BIO.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        </div>
      </Reveal>
      <Reveal delay={0.12} className="lg:col-span-3">
        <div>
          <p className="text-tiffany uppercase tracking-[0.3em] text-xs font-semibold mb-4">
            Meet Your Doctor
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-navy mb-2">
            {DOCTOR_BIO.name}
          </h2>
          <p className="text-tiffany font-medium mb-7">{DOCTOR_BIO.role}</p>
          <div className="space-y-5">
            {DOCTOR_BIO.paragraphs.map((p) => (
              <p key={p.slice(0, 40)} className="text-muted-foreground text-base leading-relaxed">
                {p}
              </p>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const TeamCard = ({ member, index }) => (
  <Reveal delay={index * 0.07}>
    <div data-testid={`team-card-${index}`} className="group text-center">
      <div className="relative aspect-[4/5] rounded-sm overflow-hidden bg-muted mb-4">
        <img
          src={member.photo}
          alt={member.name || member.role}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      {member.name && (
        <h3 className="font-serif text-xl font-semibold text-navy">{member.name}</h3>
      )}
      <p className="text-tiffany text-sm font-medium">{member.role}</p>
    </div>
  </Reveal>
);

const TeamSection = () => (
  <section data-testid="team-section" className="py-24 sm:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <p className="text-tiffany uppercase tracking-[0.3em] text-xs font-semibold mb-4">
            Meet the Team
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-navy">
            The faces behind your care
          </h2>
        </div>
      </Reveal>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {TEAM.map((m, i) => (
          <TeamCard key={m.photo} member={m} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Us"
        title="Meet the team behind your care"
        subtitle="Get to know the friendly faces at Brodheadsville Eye Care — here to look after your vision and your comfort at every visit."
        image={IMAGES.clinicInterior}
      />
      <StorySection />
      <DoctorBio />
      <TeamSection />
      <CallToAction
        title="Come See Us"
        subtitle="We'd love to welcome you to the practice. Call today — we're accepting new patients."
      />
    </div>
  );
}
